import { Box } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { forwardRef, useMemo } from 'react';
import {
  CODE_FONT, PLATE_INK, fitFont, plateCodeLabel, plateCodeRect, plateQrBox, plateQrValue,
} from '../lib/plateGeometry';
import { PlateRecipe } from '../types';

/**
 * A placa de posto composta: o gabarito ao fundo, o QR e o código por cima.
 *
 * O gerador entrega a peça com os dois espaços VAZIOS e, junto, a geometria
 * MEDIDA do render (`recipe`). Quem preenche é aqui. Por isso este componente
 * não chuta coordenada nenhuma: todo retângulo sai do JSON, e o dia em que o
 * agente desenhar a placa com o QR noutro canto isto continua certo sozinho.
 *
 * ESPAÇO DE MEDIDAS: o canvas da receita (1200×1200 nas rodadas atuais). O
 * conteúdo é autorado nesse tamanho e reduzido por `transform`, igual ao
 * `AppMockup` — é o que faz o mesmo componente servir de card pequeno, de
 * palco no diálogo e de original em resolução cheia para a captura. Reduzir
 * por CSS em vez de recalcular as coordenadas também garante que o que se vê
 * no preview é exatamente o que sai no PNG.
 *
 * É de propósito que ele não busca nada, não abre diálogo e não conhece
 * `AppArtFile`: recebe imagem, receita e código prontos. Assim serve à tela da
 * rede, a uma futura tela por posto e a um lote — que é o que precisa gerar
 * muitas placas com códigos diferentes sem passar por aqui.
 */

/* A geometria, a tinta e a tipografia moram no `plateGeometry`, porque a peça
   é composta duas vezes: aqui, em DOM, para o palco; e em canvas, para os
   arquivos que descem. Reexportados para quem já os importava daqui. */
export { PLATE_INK, plateQrValue, plateCodeLabel };

export interface StationPlateProps {
  /**
   * O gabarito, por URL. Este componente é o PALCO: os arquivos que descem não
   * saem dele, e sim do `drawPlate`, que compõe a mesma peça em canvas — então
   * aqui não há canvas para sujar nem motivo para passar data URI.
   */
  art: string | null;
  recipe: PlateRecipe;
  /**
   * O código de abastecimento cru — o mesmo que o QR codifica. A tarja o
   * imprime formatado por `plateCodeLabel`.
   */
  code: string;
  /** O que o QR codifica. Sem isto, a URL do site com o código. */
  qrValue?: string;
  /** Largura desejada em px. Sem isto, o tamanho nativo do canvas da receita. */
  width?: number;
  /** Tinta do QR e do código. */
  ink?: string;
  /**
   * Fração do quadrado do QR que o código ocupa, de 0 a 1. Sem isto, 1: o QR
   * preenche o espaço inteiro. Abaixo de 1 ele encolhe centrado e o branco do
   * gabarito vira margem em volta — o ajuste fino de quem olha o preview e
   * quer mais folga do que a receita deu.
   */
  qrScale?: number;
}

const StationPlate = forwardRef<HTMLDivElement, StationPlateProps>(({
  art, recipe, code, qrValue, width, ink = PLATE_INK, qrScale = 1,
}, ref) => {
  const canvas = recipe.canvas;
  const side = width || canvas.w;
  const scale = side / canvas.w;

  const qrBox = plateQrBox(recipe, qrScale);
  const codeRect = plateCodeRect(recipe);
  const label = plateCodeLabel(code);
  const font = useMemo(
    () => fitFont(label, codeRect, recipe.slots.codigo.fonteSugerida),
    [label, codeRect, recipe.slots.codigo.fonteSugerida],
  );

  return (
    <Box
      ref={ref}
      sx={{
        width: side, height: canvas.h * scale, flex: 'none',
        position: 'relative', overflow: 'hidden', backgroundColor: '#fff',
      }}
    >
      <Box sx={{
        width: canvas.w, height: canvas.h, position: 'relative',
        transform: `scale(${scale})`, transformOrigin: 'top left',
      }}>
        {art && (
          <Box
            component="img"
            src={art}
            alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
          />
        )}

        {/* O QR preenche o quadrado com margem 0 por padrão: o branco do
            gabarito em volta já É a zona de silêncio, e é isso que a
            `observacao` da receita manda fazer. O `qrScale` só encolhe o QR
            centrado nesse mesmo quadrado, para quem pede mais folga no
            preview — o espaço do gabarito não muda.

            `level="M"` casa com o padrão do `qrcode` do gerador — a lib do
            admin usa "L" por padrão, e a diferença muda o desenho do código. */}
        <Box sx={{
          position: 'absolute', left: qrBox.x, top: qrBox.y, width: qrBox.size, height: qrBox.size,
        }}>
          <QRCodeSVG
            value={qrValue || plateQrValue(code)}
            size={qrBox.size}
            level="M"
            marginSize={0}
            bgColor="transparent"
            fgColor={ink}
            style={{ display: 'block' }}
          />
        </Box>

        <Box sx={{
          position: 'absolute',
          left: codeRect.x, top: codeRect.y, width: codeRect.w, height: codeRect.h,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: CODE_FONT,
          fontWeight: recipe.slots.codigo.fonteSugerida.peso,
          fontSize: font.px,
          letterSpacing: `${font.spacing}px`,
          color: ink,
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}>
          {/* A margem negativa vai no TEXTO, não na tarja: `letter-spacing`
              entra depois da última letra também, e é essa sobra que puxa o
              código para a esquerda do centro. Tirá-la do item que o flex
              centraliza devolve o meio de verdade — na tarja, ela deslocaria a
              caixa inteira e a coordenada do JSON deixaria de valer. */}
          <Box component="span" sx={{ marginRight: `-${font.spacing}px` }}>{label}</Box>
        </Box>
      </Box>
    </Box>
  );
});

StationPlate.displayName = 'StationPlate';

export default StationPlate;
